import { useTranslation } from 'react-i18next';
import { useThemeContext } from '@utils/hooks/use-theme-context';
import {
  Layers,
  Activity,
  HardDrive,
  Database,
  RefreshCw,
  AlertTriangle,
  Clock,
  CheckCircle2,
  XCircle,
  Building2,
  Users,
  FileText
} from 'lucide-react';
import PageHeader from '@components/layout/page-header';
import StatWidget from '@components/back-office/shared/stat-widget';
import { useGetAdminOverview } from '@utils/api/admin/use-get-admin-overview';

/**
 * On-Prem Overview / System Health Dashboard
 * Displays queue health, job processing metrics, storage status, and database totals.
 */
export default function AdminOverview() {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();

  const { data, isLoading, isFetching, dataUpdatedAt, refetch, isError } =
    useGetAdminOverview({ refetchInterval: 30000 });

  const overview = data?.result;

  // Format last updated time
  const lastUpdated = dataUpdatedAt
    ? new Date(dataUpdatedAt).toLocaleTimeString()
    : null;

  // Helper to format numbers with locale
  const formatNumber = (num: number | undefined): string => {
    if (num === undefined || num === null) return '0';
    return num.toLocaleString();
  };

  // Helper to format minutes
  const formatMinutes = (minutes: number | undefined): string => {
    if (minutes === undefined || minutes === null) return '0 min';
    if (minutes < 1) return '<1 min';
    if (minutes < 60) return `${Math.round(minutes)} min`;
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return `${hours}h ${mins}m`;
  };

  if (isError) {
    return (
      <div className="px-4 py-2">
        <PageHeader
          title={t('overview')}
          caption={t('system-health-and-operational-metrics')}
        />
        <div
          className="rounded-2xl border p-8 text-center"
          style={{
            borderColor: getColour('border'),
            backgroundColor: getColour('secondLayer')
          }}
        >
          {}
          <XCircle className="h-12 w-12 mx-auto mb-4 text-red-500 dark:text-red-400" />
          <p className="text-sm" style={{ color: getColour('normalText') }}>
            {t('failed-to-load-overview')}
          </p>
          <button
            onClick={() => refetch()}
            className="mt-4 px-4 py-2 rounded-2xl text-sm font-medium"
            style={{
              backgroundColor: getColour('thirdLayer'),
              color: getColour('normalText')
            }}
          >
            {t('retry')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-app min-h-screen px-4 py-2">
      <div className="flex items-start justify-between mb-2">
        <PageHeader
          title={t('overview')}
          caption={t('system-health-and-operational-metrics')}
        />

        {/* Auto-refresh indicator */}
        <div className="flex items-center gap-2 text-xs mt-2">
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="flex items-center gap-1 px-2 py-1 rounded-2xl transition-colors"
            style={{
              color: getColour('inactiveText'),
              backgroundColor: isFetching
                ? getColour('thirdLayer')
                : 'transparent'
            }}
            title={t('refresh')}
          >
            <RefreshCw
              className={`h-3 w-3 ${isFetching ? 'animate-spin' : ''}`}
            />
            {lastUpdated && (
              <span style={{ color: getColour('inactiveText') }}>
                {lastUpdated}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Queue Section */}
      <section className="mb-8">
        <h2
          className="text-sm font-semibold mb-4 uppercase tracking-wide flex items-center gap-2"
          style={{ color: getColour('inactiveText') }}
        >
          <Layers className="h-4 w-4" />
          {t('queue')}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <StatWidget
            title={t('depth')}
            value={formatNumber(overview?.queue?.depth)}
            subtitle={t('total-items')}
            icon={<Layers className="h-5 w-5" />}
            variant="default"
            loading={isLoading}
          />
          <StatWidget
            title={t('processing')}
            value={formatNumber(overview?.queue?.processing)}
            subtitle={t('currently-processing')}
            icon={<Activity className="h-5 w-5" />}
            variant="success"
            loading={isLoading}
          />
          <StatWidget
            title={t('queued')}
            value={formatNumber(overview?.queue?.queued)}
            subtitle={t('waiting')}
            icon={<Clock className="h-5 w-5" />}
            variant="default"
            loading={isLoading}
          />
          <StatWidget
            title={t('failed')}
            value={formatNumber(overview?.queue?.failed)}
            subtitle={t('failed-items')}
            icon={<XCircle className="h-5 w-5" />}
            variant={(overview?.queue?.failed ?? 0) > 0 ? 'danger' : 'default'}
            loading={isLoading}
          />
          <StatWidget
            title={t('dead-letter')}
            value={formatNumber(overview?.queue?.dead_letter)}
            subtitle={t('unprocessable')}
            icon={<AlertTriangle className="h-5 w-5" />}
            variant={
              (overview?.queue?.dead_letter ?? 0) > 0 ? 'danger' : 'default'
            }
            loading={isLoading}
          />
        </div>
      </section>

      {/* Jobs Section */}
      <section className="mb-8">
        <h2
          className="text-sm font-semibold mb-4 uppercase tracking-wide flex items-center gap-2"
          style={{ color: getColour('inactiveText') }}
        >
          <Activity className="h-4 w-4" />
          {t('jobs')}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <StatWidget
            title={t('active')}
            value={formatNumber(overview?.jobs?.active)}
            subtitle={t('currently-running')}
            icon={<CheckCircle2 className="h-5 w-5" />}
            variant="success"
            loading={isLoading}
          />
          <StatWidget
            title={t('pending')}
            value={formatNumber(overview?.jobs?.pending)}
            subtitle={t('waiting-to-start')}
            icon={<Clock className="h-5 w-5" />}
            variant="default"
            loading={isLoading}
          />
          <StatWidget
            title={t('failed-24h')}
            value={formatNumber(overview?.jobs?.failed_24h)}
            subtitle={t('last-24-hours')}
            icon={<XCircle className="h-5 w-5" />}
            variant={
              (overview?.jobs?.failed_24h ?? 0) > 0 ? 'warning' : 'default'
            }
            loading={isLoading}
          />
          <StatWidget
            title={t('stranded')}
            value={formatNumber(overview?.jobs?.stranded)}
            subtitle={t('need-attention')}
            icon={<AlertTriangle className="h-5 w-5" />}
            variant={(overview?.jobs?.stranded ?? 0) > 0 ? 'danger' : 'default'}
            loading={isLoading}
          />
          <StatWidget
            title={t('avg-processing-time')}
            value={formatMinutes(overview?.jobs?.avg_processing_minutes)}
            subtitle={t('average')}
            icon={<Clock className="h-5 w-5" />}
            variant="default"
            loading={isLoading}
          />
        </div>
      </section>

      {/* Storage Section */}
      <section className="mb-8">
        <h2
          className="text-sm font-semibold mb-4 uppercase tracking-wide flex items-center gap-2"
          style={{ color: getColour('inactiveText') }}
        >
          <HardDrive className="h-4 w-4" />
          {t('storage')}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
          <StatWidget
            title={t('driver')}
            value={overview?.storage?.driver ?? '-'}
            subtitle={t('storage-backend')}
            icon={<HardDrive className="h-5 w-5" />}
            variant="default"
            loading={isLoading}
          />
          <StatWidget
            title={t('containers')}
            value={formatNumber(overview?.storage?.container_count)}
            subtitle={t('total-containers')}
            icon={<Layers className="h-5 w-5" />}
            variant="default"
            loading={isLoading}
          />
        </div>
      </section>

      {/* Database Section */}
      <section className="mb-8">
        <h2
          className="text-sm font-semibold mb-4 uppercase tracking-wide flex items-center gap-2"
          style={{ color: getColour('inactiveText') }}
        >
          <Database className="h-4 w-4" />
          {t('database')}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <StatWidget
            title={t('total-organizations')}
            value={formatNumber(overview?.database?.total_organizations)}
            subtitle={`${formatNumber(overview?.database?.active_organizations)} ${t('active')}`}
            icon={<Building2 className="h-5 w-5" />}
            variant="default"
            loading={isLoading}
          />
          <StatWidget
            title={t('total-users')}
            value={formatNumber(overview?.database?.total_users)}
            subtitle={`${formatNumber(overview?.database?.active_users)} ${t('active')}`}
            icon={<Users className="h-5 w-5" />}
            variant="default"
            loading={isLoading}
          />
          <StatWidget
            title={t('total-sessions')}
            value={formatNumber(overview?.database?.total_sessions)}
            subtitle={`${formatNumber(overview?.database?.sessions_last_30d)} ${t('last-30-days')}`}
            icon={<FileText className="h-5 w-5" />}
            variant="default"
            loading={isLoading}
          />
        </div>
      </section>
    </div>
  );
}
